/**
 * Générateur — Connaître la formule du périmètre d'un disque
 * Attendu ID : 640 | Niveau : 6ème | Domaine : Grandeurs et mesures — Les longueurs
 *
 * Types générés : qcm_unique, qcm_multiple, relier, tableau_vf, texte_trou_drag, texte_trou_saisie
 * Labels de référence : Compléter un tableau rayon-diamètre-périmètre
 * Variantes estimées : ~200+
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 640;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FORMULE = 'Compléter un tableau rayon-diamètre-périmètre';

// ─── Pools de formules (KaTeX) ───
const FORMULES_CORRECTES_D = { contenu: 'P = \\pi \\times D', label: 'P = π × D' };
const FORMULES_CORRECTES_R = { contenu: 'P = 2 \\times \\pi \\times R', label: 'P = 2 × π × R' };

const FORMULES_PIEGES = [
    { contenu: 'P = \\pi \\times R', erreur: 'oubli du facteur 2' },
    { contenu: 'P = 2 \\times \\pi \\times D', erreur: 'facteur 2 en trop avec D' },
    { contenu: 'P = \\pi \\times D^2', erreur: 'confusion avec aire (D²)' },
    { contenu: 'P = \\pi \\times R^2', erreur: 'formule de l\'aire' },
    { contenu: 'P = 2 \\times D', erreur: 'oubli de π' },
    { contenu: 'P = 2 \\times R', erreur: 'oubli de π (rayon)' },
    { contenu: 'P = \\frac{\\pi \\times D}{2}', erreur: 'division par 2 au lieu de multiplication' },
    { contenu: 'P = \\pi + D', erreur: 'addition au lieu de multiplication' },
    { contenu: 'P = \\pi + 2 \\times R', erreur: 'addition au lieu de multiplication (rayon)' },
    { contenu: 'P = 4 \\times R', erreur: 'confusion avec le périmètre d\'un carré' },
    { contenu: 'P = \\frac{\\pi \\times R}{2}', erreur: 'division par 2 avec le rayon' },
];

// Pièges spécifiques selon la donnée — pools LARGES (on en tire 3) : avec 3
// indices pour 3 tirages il n'existait qu'UNE combinaison possible → QCM
// quasi statique (2/50 uniques au crible)
const PIEGES_POUR_D = [0, 1, 2, 4, 6, 7, 9];
const PIEGES_POUR_R = [0, 1, 3, 5, 8, 9, 10];

// ─── Pool V/F ───
const AFFIRMATIONS_VRAIES = [
    'P = π × D permet de calculer le périmètre d\'un disque de diamètre D',
    'P = 2 × π × R permet de calculer le périmètre d\'un disque de rayon R',
    'Le rayon est la moitié du diamètre',
    'π ≈ 3,14',
    'Si D = 10 cm, alors R = 5 cm',
];

const AFFIRMATIONS_FAUSSES = [
    'P = π × R permet de calculer le périmètre d\'un disque',
    'P = π × R² est la formule du périmètre d\'un disque',
    'Le diamètre est la moitié du rayon',
    'P = 2 × π × D est une formule correcte du périmètre',
    'Si R = 4 cm, alors D = 2 cm',
];

// ─── Pool phrases à trous — gabarits variés (positions de trous différentes :
// chaque position teste une autre partie de la formule) ───
const PHRASES_DRAG = [
    // Gabarit 1 réécrit (revue 2026-07-31) : les 5 trous étaient tous facteurs
    // de produits — « P = D × π » ou « P = R × 2 × π », produits VRAIS, étaient
    // comptés faux (paire commutative, 3! ordres au second membre). Un seul
    // trou par formule, chacun bordé d'un élément en clair ; complémentaire du
    // gabarit 2 (positions D/2/R). « 3,14 » CONSERVÉ (décision utilisateur) :
    // « P = 3,14 × D » est une fausse ÉGALITÉ — la formule exacte utilise π,
    // 3,14 n'est qu'une approximation (l'élève tape la touche π, pas 3,14).
    // « 31,4 » en plus : l'erreur de virgule, l'autre piège diagnostique.
    {
        phrase: 'P = {{π}} × D ou encore P = 2 × π × {{R}}',
        pool: ['3,14', 'D²', 'R²', '4', 'A', '31,4'],
    },
    {
        phrase: 'P = π × {{D}} ou encore P = {{2}} × π × {{R}}',
        pool: ['D²', 'R²', '4', 'π', 'A'],
    },
    {
        phrase: 'Le périmètre d\'un disque est égal au produit du nombre {{π}} par son {{diamètre}}.',
        pool: ['rayon', 'aire', '3,14', 'carré', 'double'],
    },
    {
        phrase: 'Le périmètre d\'un disque de rayon R est égal au {{double}} du produit de {{π}} par le {{rayon}}.',
        pool: ['diamètre', 'triple', 'quart', '3,14', 'carré'],
    },
    {
        phrase: 'Si on connaît le {{rayon}} R, on calcule le périmètre avec la formule P = {{2}} × π × R.',
        pool: ['diamètre', '3', 'D', '4', 'π'],
    },
    {
        phrase: 'Si on connaît le {{diamètre}} D, on calcule le périmètre avec la formule P = {{π}} × D.',
        pool: ['rayon', '2', 'R', '3', 'aire'],
    },
    {
        phrase: 'Pour un disque, {{P}} = π × D : la lettre D désigne le {{diamètre}} et π vaut environ {{3,14}}.',
        pool: ['rayon', 'A', '31,4', '1,34', 'R'],
    },
];

// ─── Valeurs pour saisie R ↔ D ───
// (VALEURS_RD supprimé 2026-07-28 : l'ancienne saisie D = 2R était son seul
// consommateur — la saisie v2 travaille sur RAYONS_SAISIE / DIAMETRES_SAISIE.)

// ─── Générateurs par type ───

const genFormuleQcmUnique = () => {
    // Tirer au sort : question sur D ou sur R
    const surD = Math.random() < 0.5;
    const correct = surD ? FORMULES_CORRECTES_D : FORMULES_CORRECTES_R;
    const piegeSrc = surD ? PIEGES_POUR_D : PIEGES_POUR_R;
    const pieges = randomChoiceMultiple(piegeSrc, 3).map(i => FORMULES_PIEGES[i]);

    const donnee = surD ? 'diamètre D' : 'rayon R';
    const texte = randomChoice([
        `<p>Quelle est la formule du périmètre P d'un disque de ${donnee} ?</p>`,
        `<p>Le périmètre P d'un disque de ${donnee} se calcule avec la formule :</p>`,
        `<p>Parmi ces formules, laquelle donne le périmètre P d'un disque de ${donnee} ?</p>`,
        `<p>Coche la formule correcte du périmètre P d'un disque de ${donnee}.</p>`,
    ]);

    const choixRich = [
        { type: 'expression', config: { contenu: correct.contenu } },
        ...pieges.map(p => ({ type: 'expression', config: { contenu: p.contenu } })),
    ];

    const choix = shuffle(choixRich);
    const br = 'ABCDEFGH'[choix.indexOf(choixRich[0])];

    const payload = {
        type: 'qcm',
        texte,
        contenu: { choix, bonne_reponse: br },
        elements_additionnels: null,
        niveau: '6eme',
        matiere: 'maths',
        attendu: ATTENDU_ID,
        competence: 1,
        _meta: { label: L_FORMULE, type_question: 'qcm_unique' },
    };
    payload._meta.generatorKey = 'formule_perimetre';
    return payload;
};

const genFormuleQcmMultiple = () => {
    const pieges = randomChoiceMultiple([...FORMULES_PIEGES.keys()], 3).map(i => FORMULES_PIEGES[i]);
    const texte = randomChoice([
        '<p>Parmi les formules suivantes, lesquelles permettent de calculer le périmètre P d\'un disque ? (Plusieurs réponses possibles)</p>',
        '<p>Coche toutes les formules CORRECTES du périmètre P d\'un disque.</p>',
        '<p>Sélectionne les formules qui donnent bien le périmètre P d\'un disque. (Plusieurs réponses possibles)</p>',
    ]);

    const choixRich = [
        { type: 'expression', config: { contenu: FORMULES_CORRECTES_D.contenu } },
        { type: 'expression', config: { contenu: FORMULES_CORRECTES_R.contenu } },
        ...pieges.map(p => ({ type: 'expression', config: { contenu: p.contenu } })),
    ];

    const choix = shuffle(choixRich);
    const br = [
        'ABCDEFGH'[choix.indexOf(choixRich[0])],
        'ABCDEFGH'[choix.indexOf(choixRich[1])],
    ].sort();

    const payload = {
        type: 'qcm',
        texte,
        contenu: { choix, bonne_reponse: br, multi: true },
        elements_additionnels: null,
        niveau: '6eme',
        matiere: 'maths',
        attendu: ATTENDU_ID,
        competence: 1,
        _meta: { label: L_FORMULE, type_question: 'qcm_multiple' },
    };
    payload._meta.generatorKey = 'formule_perimetre';
    return payload;
};

const genFormuleRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : sources UNIVOQUES « on connaît X et
    // on cherche Y » (l'ancien « On connaît le rayon R » pouvait se relier à
    // deux formules), cible hétérogène « 3,14 » retirée (elle se triait par la
    // forme), lexique R/D/P posé dans l'énoncé, et gabarit anti-élimination
    // 2 sources / 4 cibles avec 2 leurres non appariables.
    const POOL_PAIRES = [
        { source: 'On connaît D et on cherche P', target: 'P = π × D' },
        { source: 'On connaît R et on cherche P', target: 'P = 2 × π × R' },
        { source: 'On connaît R et on cherche D', target: 'D = 2 × R' },
        { source: 'On connaît D et on cherche R', target: 'R = D ÷ 2' },
        { source: 'On connaît P et on cherche D', target: 'D = P ÷ π' },
    ];
    const pairs = randomChoiceMultiple(POOL_PAIRES, 2);

    // Leurres : formules fausses mais plausibles, jamais correctes pour AUCUNE
    // situation (non appariables par construction) — chaque code nomme l'erreur.
    const POOL_LEURRES = [
        { target: 'P = π × R', code: 'oubli_facteur_2' },
        { target: 'P = 2 × π × D', code: 'facteur_2_en_trop' },
        { target: 'D = π × R', code: 'melange_formules' },
        { target: 'R = 2 × D', code: 'double_inverse' },
        { target: 'P = π × R²', code: 'formule_aire' },
    ];
    const pris = new Set(pairs.map(p => p.target));
    const leurres = [];
    for (const l of shuffle(POOL_LEURRES)) {
        if (leurres.length >= 2) break;
        if (pris.has(l.target)) continue;
        pris.add(l.target);
        leurres.push(l);
    }

    const texte = '<p>Pour un cercle, on note R le rayon, D le diamètre et P le périmètre.</p>'
        + '<p>Relie chaque situation à la formule adaptée. Attention : certaines formules ne correspondent à aucune situation.</p>';

    const payload = makeRelier(pairs, texte, { ...opts(L_FORMULE), cibles_leurres: leurres });
    payload._meta.generatorKey = 'formule_perimetre';
    payload._meta.params = {
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

const genFormuleVF = () => {
    const vraies = randomChoiceMultiple(AFFIRMATIONS_VRAIES, 2);
    const fausses = randomChoiceMultiple(AFFIRMATIONS_FAUSSES, 2);

    const lignes = shuffle([...vraies, ...fausses]);
    const bonnesReponses = lignes.map(l => vraies.includes(l) ? 0 : 1);

    const texte = '<p>Indique si chaque affirmation est vraie ou fausse.</p>{{element:0}}';

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_FORMULE));
    payload._meta.generatorKey = 'formule_perimetre';
    return payload;
};

const genFormuleDrag = () => {
    const { phrase, pool } = randomChoice(PHRASES_DRAG);
    // 3 distracteurs tirés du pool du gabarit, filtrés contre les mots attendus
    const distracteurs = randomChoiceMultiple(filtrerDistracteurs(phrase, pool), 3);
    const texte = '<p>Complète avec les éléments proposés.</p>';

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_FORMULE), distracteurs });
    payload._meta.generatorKey = 'formule_perimetre';
    return payload;
};

// ─── texte_trou_saisie v2 — « Connaître la formule du périmètre d'un disque » ───
//
// L'ancien gabarit ne faisait que D = 2 × R (ni π, ni périmètre — hors attendu,
// C1 bloquant du rapport). v2 (simulateur HTML validé) : la saisie parcourt la
// chaîne complète FORMULE → SUBSTITUTION → CALCUL, en deux variantes :
//   - « rayon donné »    : P = {{2}} × π × R (le coefficient EST la formule),
//                          puis P ≈ 2 × 3,14 × {{R}} ≈ {{P}} cm — le trou de
//                          substitution est LE piège (y mettre D double le résultat) ;
//   - « diamètre donné » : R = {{D/2}} cm, puis P ≈ 3,14 × {{D}} ≈ {{P}} cm —
//                          y mettre R donne la moitié (confusion type, C5).
// Valeurs choisies pour un calcul propre : R entier ou en ,5 → D entier, et
// P = 3,14 × D a exactement 2 décimales. π ≈ 3,14 rappelé, ≈ = arrondi.

/** Rayons « propres » (entiers ou demi-entiers) et diamètres entiers. */
const RAYONS_SAISIE = ['2', '3', '4', '5', '6', '8', '10', '12', '2,5', '3,5', '4,5', '7,5'];
const DIAMETRES_SAISIE = ['4', '6', '7', '8', '9', '10', '11', '12', '14', '15', '20'];

const saisieNum = (s) => parseFloat(String(s).replace(',', '.'));
const saisieFr = (x) => String(x).replace('.', ',');

/** 3,14 × D (D entier) en arithmétique ENTIÈRE : centièmes = 314 × D. */
const perimetre314 = (dEntier) => {
    const cents = 314 * dEntier;
    const ent = Math.floor(cents / 100);
    const dec = String(cents % 100).padStart(2, '0').replace(/0+$/, '');
    return dec === '' ? String(ent) : `${ent},${dec}`;
};

/** Figure : disque avec rayon ou diamètre coté (data-URI, idiome gen_614). */
const buildDisqueDataUrl = (label, valFr) => {
    const cote = label === 'R'
        ? '<line x1="75" y1="65" x2="123" y2="65" stroke="currentColor" stroke-width="2"/>'
            + `<text x="99" y="55" text-anchor="middle" font-size="14" font-weight="600" fill="currentColor">R = ${valFr} cm</text>`
        : '<line x1="27" y1="65" x2="123" y2="65" stroke="currentColor" stroke-width="2"/>'
            + `<text x="75" y="55" text-anchor="middle" font-size="14" font-weight="600" fill="currentColor">D = ${valFr} cm</text>`;
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 130" width="150" height="130">'
        + '<circle cx="75" cy="65" r="48" fill="none" stroke="currentColor" stroke-width="2"/>'
        + '<circle cx="75" cy="65" r="2.5" fill="currentColor"/>' + cote + '</svg>';
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const genFormuleSaisie = () => {
    const donneR = randInt(0, 1) === 1;
    let phrase; let texte; let figure;

    if (donneR) {
        const rStr = randomChoice(RAYONS_SAISIE);
        const d = Math.round(saisieNum(rStr) * 2);            // toujours entier (pool)
        const pStr = perimetre314(d);
        texte = `<p>Un disque a un <b>rayon R = ${rStr} cm</b>. On prend <b>π ≈ 3,14</b>.</p>`
            // Consigne SOBRE (revue 2026-07-31) : l'ancienne mise en garde
            // (« attention à bien remplacer R... ») désamorçait le piège que le
            // format mesure — la vigilance R/D est l'affaire du coup de pouce.
            + '<p>Complète la formule, puis le calcul.</p>';
        phrase = `La formule du périmètre à partir du rayon : P = {{2}} × π × R.\n`
            + `Calcul : P ≈ 2 × 3,14 × {{${rStr}}} ≈ {{${pStr}}} cm.`;
        figure = buildDisqueDataUrl('R', rStr);
    } else {
        const dStr = randomChoice(DIAMETRES_SAISIE);
        const d = saisieNum(dStr);
        const rStr = saisieFr(d / 2);
        const pStr = perimetre314(d);
        texte = `<p>Un disque a un <b>diamètre D = ${dStr} cm</b>. On prend <b>π ≈ 3,14</b>.</p>`
            + '<p>Complète le raisonnement, puis le calcul.</p>';
        phrase = `Son rayon vaut R = {{${rStr}}} cm.\n`
            + `La formule du périmètre à partir du diamètre : P = π × D.\n`
            + `Calcul : P ≈ 3,14 × {{${dStr}}} ≈ {{${pStr}}} cm.`;
        figure = buildDisqueDataUrl('D', dStr);
    }

    const payload = {
        ...makeTexteTrou(phrase, texte, { ...opts(L_FORMULE), mode: 'saisie' }),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl: figure, maxWidth: 'small' } }],
        },
    };
    // Calculatrice AUTORISÉE (revue 2026-07-31) : on calcule avec π — le focus
    // est la formule et la substitution, pas la multiplication décimale posée.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'formule_perimetre';
    return payload;
};


// ─── Registre ───
const GENERATORS = {
    formule_perimetre_qcm_unique: genFormuleQcmUnique,
    formule_perimetre_qcm_multiple: genFormuleQcmMultiple,
    formule_perimetre_relier: genFormuleRelier,
    formule_perimetre_tableau_vf: genFormuleVF,
    formule_perimetre_texte_trou_drag: genFormuleDrag,
    formule_perimetre_texte_trou_saisie: genFormuleSaisie,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'formule_perimetre', label: L_FORMULE, formatsList: buildFormats('formule_perimetre') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
